import { useRef } from "react";

/** The three stages of a trade, in the order one actually moves through them. */
export type TradePaneKey = "potential" | "proposed" | "completed";

export const TRADE_PANES: Array<{ key: TradePaneKey; label: string }> = [
  { key: "potential", label: "Potential" },
  { key: "proposed", label: "Proposed" },
  { key: "completed", label: "Completed" },
];

export const tradeTabId = (key: TradePaneKey) => `trade-tab-${key}`;

/**
 * The segmented control that names the three panes and says which one you are
 * on. It is the accessible route between panes — the swipe is an accelerator,
 * not the only way through, so this carries the real tablist semantics and
 * arrow-key handling.
 *
 * Counts sit on the tabs rather than inside each pane's body, so a glance at
 * the strip tells you whether anything is waiting on the pane you are NOT
 * looking at. `null` means "still loading" and renders no chip at all rather
 * than a zero that would later change.
 *
 * ON `interactive`. The strip lives INSIDE each pane, under that pane's own
 * heading, so it travels with the page you are looking at. That means more than
 * one copy is in the DOM at once (off-stage panes stay mounted so swiping back
 * is instant), and element ids must be unique — two `#trade-tab-potential`s
 * would leave every aria-controls/labelledby reference resolving to whichever
 * came first. So exactly one strip, the active pane's, carries the ids and the
 * tablist role; the copies inside off-stage panes are inert scenery that exists
 * only so the incoming page looks complete while it slides in.
 */
export function TradeHubTabs({
  active,
  onSelect,
  counts,
  interactive = true,
}: {
  active: TradePaneKey;
  onSelect: (key: TradePaneKey) => void;
  counts: Partial<Record<TradePaneKey, number | null>>;
  /** False for the copies inside off-stage panes — see the note above. */
  interactive?: boolean;
}) {
  const stripRef = useRef<HTMLDivElement | null>(null);

  const move = (dir: -1 | 1) => {
    const at = TRADE_PANES.findIndex((p) => p.key === active);
    const next = TRADE_PANES[Math.max(0, Math.min(TRADE_PANES.length - 1, at + dir))];
    if (next && next.key !== active) {
      onSelect(next.key);
      // Follow the selection with focus so arrow-keying does not leave the
      // focus ring behind on the tab you just left.
      requestAnimationFrame(() => {
        stripRef.current
          ?.querySelector<HTMLButtonElement>(`#${CSS.escape(tradeTabId(next.key))}`)
          ?.focus();
      });
    }
  };

  return (
    <div
      ref={stripRef}
      role={interactive ? "tablist" : "presentation"}
      aria-label={interactive ? "Trade stage" : undefined}
      className="flex gap-1 p-1 rounded-full"
      style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}
      data-testid={interactive ? "trade-hub-tabs" : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "ArrowRight") { e.preventDefault(); move(1); }
              if (e.key === "ArrowLeft") { e.preventDefault(); move(-1); }
            }
          : undefined
      }
    >
      {TRADE_PANES.map((p) => {
        const on = p.key === active;
        const count = counts[p.key];
        return (
          <button
            key={p.key}
            id={interactive ? tradeTabId(p.key) : undefined}
            type="button"
            role={interactive ? "tab" : "presentation"}
            aria-selected={interactive ? on : undefined}
            aria-controls={interactive ? `trade-pane-${p.key}` : undefined}
            tabIndex={interactive && on ? 0 : -1}
            onClick={interactive ? () => onSelect(p.key) : undefined}
            data-testid={interactive ? `trade-hub-tab-${p.key}` : undefined}
            className="flex-1 min-w-0 rounded-full py-2 px-2 text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5 transition-colors"
            style={{
              background: on ? "var(--accent-fill)" : "transparent",
              color: on ? "var(--text-on-accent)" : "var(--text-secondary)",
            }}
          >
            <span className="truncate">{p.label}</span>
            {count != null && count > 0 && (
              <span
                className="text-[9.5px] font-mono tabular-nums px-1.5 py-0.5 rounded-full leading-none shrink-0"
                style={{
                  background: on ? "hsl(0 0% 0% / 0.18)" : "var(--surface-raised)",
                  color: on ? "var(--text-on-accent)" : "var(--text-tertiary)",
                }}
                data-testid={interactive ? `trade-hub-tab-${p.key}-count` : undefined}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
